import { createRouter, createWebHistory } from "vue-router";
import { useUserStore } from '@/store/modules/user'
import { useRecentVisitsStore } from '@/store/modules/recent-visits'
import { buildMenuCatalog } from '@/utils/menu-catalog'
import { recordMenuVisit } from '@/api/modules/sys/menu-heat'
import { refreshTokenWithParams } from '@/api/modules/sys/sys-user'
import { fixedAuthenticatedRoutes, excludeFixedRoutes } from './fixed-routes.js'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    ...fixedAuthenticatedRoutes,
    { path: '/cli/authorize', name: 'CliAuthorize', component: () => import('@/views/cli/authorize.vue'), meta: { title: 'CLI 授权', hidden: true, requiresAuth: true } },
    {
      path: '/login',
      name: 'Login',
      component: () => import('../views/login/index.vue'),
      meta: {
        title: '登录',
        hidden: true // 在菜单中隐藏该路由
      }
    },
    {
      path: '/share/agent/:shareCode',
      name: 'AgentChatPublicShare',
      component: () => import('@/views/agent-chat/share.vue'),
      meta: {
        title: '分享的森森对话',
        hidden: true,
        public: true
      }
    },
    // 首页路由（在动态路由加载前作为占位路由）
    {
      path: '/',
      name: 'Home',
      component: () => import('@/layout/index.vue'),
      meta: {
        title: '首页',
        requiresAuth: true
      },
      children: [{
        path: "",
        name: "home",
        icon: 'home',
        meta: {
          title: "页面",
        },
        component: () => import("../views/home/index.vue"),
      }]
    }
  ],
});

const parseURL = (url) => {
  // 如果输入为 null 或 undefined，返回空字符串和空对象
  if (url == null) {
    return { basePath: '', paramsObject: {} }
  }

  // 找到问号 (?) 的位置
  const questionMarkIndex = url.indexOf('?')
  let basePath = url
  const paramsObject = {}

  // 如果存在查询参数
  if (questionMarkIndex !== -1) {
    // 基础路径
    basePath = url.substring(0, questionMarkIndex)

    // 查询字符串
    const queryString = url.substring(questionMarkIndex + 1)

    // 解析参数
    const searchParams = new URLSearchParams(queryString)
    searchParams.forEach((value, key) => {
      paramsObject[key] = value
    })
  }

  // ⚠️ 原代码缺少的 return
  return { basePath, paramsObject }
}


// 路由守卫
router.beforeEach(async (to, from, next) => {

  // 获取地址栏数据
  const { paramsObject: query } = parseURL(to.fullPath)
  const authToken = query['Authorization']  
  if(authToken ) {
    // 3. 登录系统
      // 调用后端登录接口，传入Authorization参数
    const response = await refreshTokenWithParams(authToken)
   
   
    const userStore = useUserStore()
    // 登录成功后保存token
    userStore.setToken(response.data.accessToken)
    // 仅设置基础用户ID信息，完整信息将在getPermissionInfo中获取   
    userStore.setUserInfo({
      id: response.data.userId,
    })    
    await userStore.getPermissionInfo()

    // 动态路由是在本次导航解析完成后注册的，需要重新进入目标地址进行匹配。
    await addDynamicRoutes(userStore, to, next)
    return
  }

  if (to.meta.public) {
    next()
    return
  }

  
  const token = localStorage.getItem('token')

  // 白名单路由，不需要登录就可以访问
  const whiteList = ['/login']

  if (whiteList.includes(to.path)) {
    // 如果已登录且要去登录页，重定向到首页
    if (token) {
      next('/')
    } else {
      next()
    }
  } else {
    if (!token) {
      // 没有token，重定向到登录页
      next({ path: '/login', query: { redirect: to.fullPath } })
    } else {
      const userStore = useUserStore()

      // 检查是否已获取权限信息
      if (!userStore.roles.length && !userStore.permissions.length) {
        // 第一次登录或页面刷新后首次访问：获取权限并生成动态路由
        try {
          await userStore.getPermissionInfo()
          await addDynamicRoutes(userStore, to, next)
        } catch (error) {
          // 获取权限信息失败，可能是token过期
          userStore.resetState()
          next({ path: '/login', query: { redirect: to.fullPath } })
        }
      } else {
        // 已有权限信息，但需要确保动态路由已添加
        // 检查路由表中是否已有动态路由（登录后首次访问其他页面时可能还没添加）
        if (userStore.menus && userStore.menus.length > 0) {
          // 目标地址可能在本次导航开始后才注册，交给辅助函数在必要时重新匹配。
          await addDynamicRoutes(userStore, to, next)
          return
        }
        next()
      }
    }
  }
})

/**
 * 添加动态路由的辅助函数
 * @param {Object} userStore - 用户Store实例
 * @param {Object} to - 目标路由（可选，用于首次登录后的跳转）
 * @param {Function} next - 路由守卫next函数（可选）
 */
async function addDynamicRoutes(userStore, to, next) {
  const { generateRoutesFromMenus } = await import('@/utils/permission.js')

  const dynamicRoutes = excludeFixedRoutes(generateRoutesFromMenus(userStore.menus))

  let hasAddedNewRoute = false;

  // 添加动态路由
  dynamicRoutes.forEach(route => {
    if (!router.hasRoute(route.name)) {
      router.addRoute(route)
      hasAddedNewRoute = true;
    }
  })

  // 添加404路由到末尾
  if (!router.hasRoute('404Page')) {
    router.addRoute({
      path: '/:path(.*)*',
      name: '404Page',
      component: () => import('@/views/error/404.vue'),
      meta: {
        hidden: true,
        breadcrumb: false
      }
    })
  }

  // 只有在添加了新路由时才重新导航，避免无限循环
  if (to && next && hasAddedNewRoute) {
    next({ ...to, replace: true })
  } else if (to && next) {
    // 如果没有添加新路由，直接调用 next()，不重新导航
    next()
  }
}

router.afterEach((to, from, failure) => {
  if (failure) return
  const user = useUserStore()
  if (!user.token || user.userId == null) return
  const items = buildMenuCatalog(user.menus).flatMap(category => category.items)
  const item = items.find(item => item.path === to.path)
    || (to.path.startsWith('/chat/agent/') ? items.find(item => item.path === '/chat/agent') : null)
  if (item) {
    useRecentVisitsStore().record(user.userId, item.path)
    const previousItem = items.find(entry => entry.path === from.path)
      || (from.path.startsWith('/chat/agent/') ? items.find(entry => entry.path === '/chat/agent') : null)
    // 同一菜单内切换参数/会话不重复计数；新标签、刷新和再次进入均由目标页记录一次。
    if (item.menuId != null && previousItem?.path !== item.path) {
      recordMenuVisit(item.menuId).catch(() => { /* 统计异常不阻塞导航，不重试以避免重复计数。 */ })
    }
  }
})

// 暴露菜单项，供侧边栏或顶部导航栏使用
export const menuRoutes = router.routes;

export default router;
